import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getTicket, analyzeTicket } from '../api/scout';
import { LoadingOverlay, Spinner } from '../components/LoadingState';
import ConversationTimeline from '../components/ConversationTimeline';
import AnalysisCard from '../components/AnalysisCard';
import { useTheme, ACCENT } from '../contexts/ThemeContext';

function saveToRecent(ticketId, summary) {
  try {
    const recent = JSON.parse(localStorage.getItem('scout_recent') || '[]');
    const filtered = recent.filter(r => r.ticketId !== ticketId);
    filtered.unshift({
      ticketId,
      summary: summary || 'Viewed',
      timestamp: format(new Date(), 'MMM d, h:mm a'),
    });
    localStorage.setItem('scout_recent', JSON.stringify(filtered.slice(0, 20)));
  } catch { /* ignore */ }
}

function CaraBadge({ cara }) {
  if (!cara?.involved) return null;
  const color = '#3DD4E8';
  let label = `CARA INVOLVED (${cara.publicMessages} MSG)`;
  if (cara.resolvedByCARA) label = 'CARA RESOLVED';
  else if (!cara.resolvedByCARA && cara.resolvedBy) label = `CARA → ${cara.resolvedBy}`;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.08em', padding: '2px 8px', borderRadius: '6px',
      background: `${color}15`, color,
      borderLeft: `2px solid ${color}`,
    }}>
      {label}
    </span>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const { t } = useTheme();
  const [ticketData, setTicketData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTicket(id);
      setTicketData(data.data);
      saveToRecent(id, data.data?.ticket?.subject);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  async function handleAnalyze() {
    setAnalyzing(true);
    try {
      const data = await analyzeTicket(id);
      setAnalysis(data.analysis);
      if (data.data) setTicketData(data.data);
      saveToRecent(id, data.analysis?.summary || ticketData?.ticket?.subject);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  }

  function copyReport() {
    if (!analysis) return;
    const ticket = ticketData?.ticket;
    let md = `## Ticket #${id}\n`;
    md += `**Subject:** ${ticket?.subject || 'N/A'}\n`;
    md += `**Category:** ${analysis.category}\n`;
    md += `**Severity:** ${analysis.severity}\n\n`;
    md += `**Summary:** ${analysis.summary}\n\n`;
    md += `**Assessment:** ${analysis.assessment}\n\n`;
    if (analysis.recommendation) md += `**Recommendation:** ${analysis.recommendation}\n`;
    navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) return <LoadingOverlay message={`Loading ticket #${id}...`} />;

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div style={{
          padding: '12px 16px', borderRadius: '10px',
          background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)',
          borderLeft: '2px solid #F07070',
          color: '#F07070', fontSize: '12px', fontWeight: 600,
        }}>{error}</div>
      </div>
    );
  }

  const ticket = ticketData?.ticket;
  const comments = ticketData?.comments || [];
  const satisfaction = ticketData?.satisfaction;
  const users = ticketData?.users || {};
  const cara = ticketData?.cara;
  const assignee = ticketData?.assignee;
  const requester = ticketData?.requester;

  const STATUS_COLORS = {
    new: '#50D080', open: '#5B9BF7', pending: '#E5C040',
    hold: '#F0923C', solved: '#6A6A70', closed: '#4A4A50',
  };

  const statusColor = STATUS_COLORS[ticket?.status] || '#4A4A50';
  const csatColor = satisfaction?.score === 'bad' ? '#F07070' : satisfaction?.score === 'good' ? '#50D080' : t.textMuted;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Ticket header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            fontSize: '9px', fontWeight: 900, color: t.textDim,
            letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px',
          }}>
            TICKET
          </div>
          <h1 style={{
            fontSize: '18px', fontWeight: 900, color: t.text,
            letterSpacing: '0.02em', lineHeight: 1.3,
          }}>
            <span className="font-mono" style={{ color: ACCENT }}>#{ticket?.id}</span>
            <span style={{ margin: '0 10px', color: t.textFaint }}>/</span>
            {ticket?.subject || 'No subject'}
          </h1>
        </div>

        {/* Meta */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '2px 8px', borderRadius: '6px',
            fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.1em',
            background: `${statusColor}15`, color: statusColor,
            borderLeft: `2px solid ${statusColor}`,
          }}>
            {ticket?.status}
          </span>
          {ticket?.priority && (
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '2px 8px', borderRadius: '6px',
              fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.1em',
              background: 'rgba(255,255,255,0.03)', color: t.textMuted,
              border: `1px solid ${t.border}`,
            }}>
              {ticket.priority}
            </span>
          )}
          {ticket?.created_at && (
            <span className="font-mono" style={{ fontSize: '10px', color: t.textDim }}>
              {format(new Date(ticket.created_at), 'MMM d, yyyy h:mm a')}
            </span>
          )}
          <CaraBadge cara={cara} />
        </div>

        {/* People */}
        <div style={{
          display: 'flex', gap: '24px', marginTop: '14px', paddingTop: '14px',
          borderTop: `1px solid ${t.border}`,
        }}>
          {requester && (
            <div>
              <span style={{
                fontSize: '9px', fontWeight: 700, color: t.textFaint,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                display: 'block', marginBottom: '2px',
              }}>Requester</span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: t.textMuted }}>{requester.name}</span>
            </div>
          )}
          {assignee && (
            <div>
              <span style={{
                fontSize: '9px', fontWeight: 700, color: t.textFaint,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                display: 'block', marginBottom: '2px',
              }}>Assignee</span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: t.textMuted }}>{assignee.name}</span>
            </div>
          )}
        </div>

        {/* CSAT */}
        {satisfaction && (
          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${t.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '9px', fontWeight: 700, color: t.textFaint,
                letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>CSAT</span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: csatColor, letterSpacing: '0.02em' }}>
                {satisfaction.score === 'bad' ? 'BAD' : satisfaction.score === 'good' ? 'GOOD' : satisfaction.score?.toUpperCase()}
              </span>
            </div>
            {satisfaction.comment && (
              <p className="quote-border" style={{ fontSize: '13px', marginTop: '10px', lineHeight: 1.55 }}>
                "{satisfaction.comment}"
              </p>
            )}
          </div>
        )}
      </div>

      {/* Two column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Conversation */}
        <div className="lg:col-span-2">
          <span style={{
            fontSize: '10px', fontWeight: 900, color: t.textDim,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            display: 'block', marginBottom: '12px',
          }}>
            CONVERSATION ({comments.length})
          </span>
          <ConversationTimeline comments={comments} users={users} />
        </div>

        {/* Right: Analysis */}
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '12px',
          }}>
            <span style={{
              fontSize: '10px', fontWeight: 900, color: t.textDim,
              letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>
              AI ANALYSIS
            </span>
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              className="btn-primary"
              style={{ height: '34px', fontSize: '10px', padding: '0 16px' }}
            >
              {analyzing ? <Spinner size="sm" /> : null}
              {analysis ? 'RE-ANALYZE' : 'ANALYZE'}
            </button>
          </div>

          {analyzing && <LoadingOverlay message="Analyzing..." />}

          {analysis && !analyzing && (
            <>
              <AnalysisCard analysis={analysis} />
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <a
                  href={`https://coroshelp.zendesk.com/agent/tickets/${ticket?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ textAlign: 'center', fontSize: '10px', display: 'block', textDecoration: 'none' }}
                >
                  OPEN IN ZENDESK
                </a>
                <button onClick={copyReport} className="btn-secondary" style={{ width: '100%', fontSize: '10px' }}>
                  {copied ? 'COPIED' : 'COPY REPORT'}
                </button>
              </div>
            </>
          )}

          {!analysis && !analyzing && (
            <div className="card" style={{ textAlign: 'center', padding: '36px 20px' }}>
              <p style={{
                color: t.textDim, fontSize: '11px', marginBottom: '14px',
                fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                Click "Analyze" to run AI categorization
              </p>
              <a
                href={`https://coroshelp.zendesk.com/agent/tickets/${ticket?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: ACCENT, fontSize: '11px', fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}
              >
                OPEN IN ZENDESK
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
